import { useMutation } from '@apollo/client'
import { useEffect, useState, useRef } from 'react'
import { PREPARE_CREATE_LESSON_FOR_STUDENT } from '../mutations'

export const usePrepareLessonForStudent = ({
  lessonTypeId,
  locationId,
  startDateTime,
  studentId,
  skipPayment,
  enabled = true,
  shouldExcludeSkipPayment = false,
}) => {
  const [clientSecret, setClientSecret] = useState(null)
  // null = never fetched, non-null = has fetched and stores the last skipPayment value
  const lastFetchedSkipPaymentRef = useRef(null)
  const previousEnabledRef = useRef(enabled)
  const isFetchingRef = useRef(false)
  const skipPaymentAtFetchTimeRef = useRef(null)
  const [prepareLessonForStudent, { data, loading, error }] = useMutation(
    PREPARE_CREATE_LESSON_FOR_STUDENT,
  )

  useEffect(() => {
    // Track when enabled transitions from false to true (user clicked "Complete Purchase")
    const enabledJustBecameTrue = !previousEnabledRef.current && enabled
    previousEnabledRef.current = enabled

    if (
      !enabled ||
      !lessonTypeId ||
      !locationId ||
      !startDateTime ||
      !studentId
    ) {
      return
    }

    // Check if skipPayment changed since last successful fetch
    const hasFetched = lastFetchedSkipPaymentRef.current !== null
    const skipPaymentChanged =
      hasFetched && lastFetchedSkipPaymentRef.current !== skipPayment

    // For payment required (!skipPayment), fetch once on initial load
    // For skip payment (skipPayment), use the complex logic to handle toggle scenarios
    const hasFetchedForCurrentSkipPayment =
      hasFetched && lastFetchedSkipPaymentRef.current === skipPayment

    let shouldFetch
    if (!skipPayment) {
      // Payment required: fetch only once on initial load (when we haven't fetched yet)
      shouldFetch = !hasFetchedForCurrentSkipPayment
    } else {
      // Skip payment: use existing logic
      const skipPaymentChangedToTrue =
        skipPaymentChanged &&
        lastFetchedSkipPaymentRef.current === false &&
        skipPayment === true
      shouldFetch =
        enabledJustBecameTrue ||
        (skipPaymentChanged && !skipPaymentChangedToTrue) ||
        (skipPaymentChangedToTrue && enabledJustBecameTrue)
    }

    if (shouldFetch && !loading && !isFetchingRef.current) {
      // Mark that we're fetching to prevent duplicate calls
      isFetchingRef.current = true
      // Capture skipPayment value at fetch time to use later in success handler
      skipPaymentAtFetchTimeRef.current = skipPayment
      // Clear clientSecret if skipPayment changed (not on initial fetch)
      if (skipPaymentChanged) {
        setClientSecret(null)
      }

      const input = {
        lessonTypeId,
        locationId,
        startDateTime,
        studentId,
      }
      if (!shouldExcludeSkipPayment) {
        input.skipPayment = skipPayment
      }

      prepareLessonForStudent({
        variables: {
          input,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    lessonTypeId,
    locationId,
    startDateTime,
    studentId,
    skipPayment,
    shouldExcludeSkipPayment,
    prepareLessonForStudent,
    // Note: loading is checked in the condition (!loading) but intentionally
    // not in deps to avoid unnecessary re-runs when loading state changes.
    // The effect should only run when actual parameters change, not when
    // loading state changes.
  ])

  // Update refs and clientSecret when mutation succeeds
  useEffect(() => {
    if (data?.prepareCreateLessonForStudent?.success) {
      const paymentIntent = data.prepareCreateLessonForStudent.paymentIntent
      setClientSecret(paymentIntent?.clientSecret)
      // Update refs only after successful fetch
      // Use the skipPayment value that was used when the mutation was initiated,
      // not the current value (which may have changed while mutation was in flight)
      if (skipPaymentAtFetchTimeRef.current !== null) {
        lastFetchedSkipPaymentRef.current = skipPaymentAtFetchTimeRef.current
        skipPaymentAtFetchTimeRef.current = null
      }
      // Reset fetching flag
      isFetchingRef.current = false
    }
  }, [data])

  // Reset fetching flag when loading becomes false (in case of error)
  useEffect(() => {
    if (!loading) {
      isFetchingRef.current = false
    }
  }, [loading])

  const success = data?.prepareCreateLessonForStudent?.success ?? null
  const message = data?.prepareCreateLessonForStudent?.message ?? null
  return { clientSecret, setClientSecret, loading, error, success, message }
}
